import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import RichTextEditor from "@/components/RichTextEditor";

const SOURCE_OPTIONS = [
  "LinkedIn",
  "Polecenie",
  "Własne kręgi",
  "Konferencja/Targi",
  "Strona WWW",
  "Cold outreach (e-mail/tel.)",
  "Media społecznościowe",
  "Inne",
];

const COUNTRY_OPTIONS = [
  "Polska",
  "Kenia",
  "Tanzania",
  "Uganda",
  "Rwanda",
  "Burundi",
  "DR Kongo",
  "Zambia",
  "Etiopia",
  "Sudan Południowy",
  "Somalia",
];

const EU_COUNTRIES = new Set([
  "Polska", "Niemcy", "Francja", "Włochy", "Hiszpania", "Holandia", "Niderlandy", "Belgia",
  "Austria", "Czechy", "Słowacja", "Litwa", "Łotwa", "Estonia", "Irlandia", "Portugalia",
  "Szwecja", "Dania", "Finlandia", "Rumunia", "Bułgaria", "Węgry", "Chorwacja", "Słowenia",
  "Grecja", "Luksemburg", "Malta", "Cypr",
]);

const NIP_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7];
const nipDigits = (raw) => (raw || "").replace(/\D/g, "");
const nipChecksumOk = (raw) => {
  const d = nipDigits(raw);
  if (d.length !== 10) return false;
  const sum = NIP_WEIGHTS.reduce((a, w, i) => a + w * parseInt(d[i], 10), 0);
  const c = sum % 11;
  return c !== 10 && c === parseInt(d[9], 10);
};

const VAT_CLS = { "Czynny": "bg-emerald-100 text-emerald-700", "Zwolniony": "bg-amber-100 text-amber-700" };
const VatBadge = ({ status }) => (
  <span data-testid="vat-badge" className={`rounded-full text-[10px] uppercase tracking-wider px-2 py-0.5 font-[Manrope] font-medium ${VAT_CLS[status] || "bg-red-100 text-red-700"}`}>
    VAT: {status}
  </span>
);

const normalizeIban = (raw) => (raw || "").replace(/\s+/g, "").toUpperCase();
const formatIban = (raw) => normalizeIban(raw).replace(/(.{4})/g, "$1 ").trim();
const ibanToCheckString = (v) => (/^\d{26}$/.test(v) ? "PL" + v : v);
const ibanMod97 = (v) => {
  const rearranged = v.slice(4) + v.slice(0, 4);
  const converted = rearranged.replace(/[A-Z]/g, (c) => (c.charCodeAt(0) - 55).toString());
  let mod = 0;
  for (const ch of converted) mod = (mod * 10 + parseInt(ch, 10)) % 97;
  return mod === 1;
};
const ibanValid = (raw) => {
  const v = ibanToCheckString(normalizeIban(raw));
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(v)) return false;
  if (v.length < 15 || v.length > 34) return false;
  return ibanMod97(v);
};
const ibanLooksComplete = (raw) => {
  const n = normalizeIban(raw);
  return /^\d{26}$/.test(n) || /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(n);
};

const empty = {
  company_name: "",
  contact_person: "",
  email: "",
  phone: "",
  region: "Poland",
  country: "",
  status: "Lead",
  subscription: "None",
  annual_value: 0,
  address: "",
  nip: "",
  source: "",
  regon: "",
  krs: "",
  bank_account: "",
  swift_bic: "",
  notes: "",
};

export default function ClientForm({ open, onOpenChange, client, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [nipChecking, setNipChecking] = useState(false);
  const [vatStatus, setVatStatus] = useState(null);
  const [countryCustom, setCountryCustom] = useState(false);
  const isEdit = !!client;

  useEffect(() => {
    if (open) {
      setForm(client ? { ...empty, ...client, bank_account: formatIban(client.bank_account || "") } : empty);
      setVatStatus(null);
      setCountryCustom(!!(client?.country && !COUNTRY_OPTIONS.includes(client.country)));
    }
  }, [open, client]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const country = (form.country || "").trim();
  const isPoland = country ? country === "Polska" : form.region === "Poland";
  const isEU = country ? EU_COUNTRIES.has(country) : form.region === "Poland";

  const nipRaw = nipDigits(form.nip);
  const nipInvalid = isPoland && nipRaw.length === 10 && !nipChecksumOk(nipRaw);
  const bankInvalid = isEU && ibanLooksComplete(form.bank_account) && !ibanValid(form.bank_account);

  const lookupNip = async () => {
    const raw = nipDigits(form.nip);
    if (raw.length !== 10) {
      toast.error("NIP musi mieć 10 cyfr");
      return;
    }
    if (!nipChecksumOk(raw)) {
      toast.error("Nieprawidłowa suma kontrolna NIP");
      return;
    }
    setNipChecking(true);
    try {
      const { data } = await api.get(`/nip/${raw}`);
      if (!data.found) {
        toast.warning("NIP poprawny, ale nie znaleziono firmy w rejestrze MF");
        return;
      }
      setForm((f) => {
        const next = { ...f, nip: raw };
        if (!f.company_name?.trim() && data.name) next.company_name = data.name;
        if (!f.address?.trim() && data.address) next.address = data.address;
        if (!f.regon?.trim() && data.regon) next.regon = data.regon;
        if (!f.krs?.trim() && data.krs) next.krs = data.krs;
        if (!f.bank_account?.trim() && data.bank_accounts?.length) next.bank_account = formatIban(data.bank_accounts[0]);
        return next;
      });
      if (form.company_name?.trim() && data.name && form.company_name.trim().toLowerCase() !== data.name.toLowerCase()) {
        toast.info(`Rejestr MF: „${data.name}" — porównaj z wpisaną nazwą`);
      }
      setVatStatus(data.status_vat || null);
      toast.success(`Zweryfikowano: ${data.name}${data.status_vat ? " · VAT: " + data.status_vat : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd weryfikacji NIP");
    } finally {
      setNipChecking(false);
    }
  };

  const submit = async () => {
    if (!form.company_name.trim()) {
      toast.error("Company name is required");
      return;
    }
    if (nipInvalid) {
      toast.error("Nieprawidłowy NIP — popraw sumę kontrolną");
      return;
    }
    if (bankInvalid) {
      toast.error("Nieprawidłowy numer konta (błędna suma kontrolna IBAN)");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        company_name: form.company_name,
        contact_person: form.contact_person,
        email: form.email,
        phone: form.phone,
        region: form.region,
        country: form.country,
        status: form.status,
        subscription: form.subscription,
        annual_value: form.annual_value,
        address: form.address,
        nip: form.nip,
        source: form.source,
        regon: form.regon,
        krs: form.krs,
        bank_account: form.bank_account,
        swift_bic: form.swift_bic,
        notes: form.notes,
      };
      if (isEdit) await api.put(`/clients/${client.client_id}`, payload);
      else await api.post("/clients", payload);
      toast.success(isEdit ? "Client updated" : "Client added");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error("Failed to save client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-2xl tracking-tight">
            {isEdit ? "Edit Client" : "New Client"}
          </DialogTitle>
          <DialogDescription className="font-[Manrope]">
            {isEdit ? "Update the client's details and notes." : "Add a new client to your CRM."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div className="sm:col-span-2">
            <Label className="font-[Manrope] text-sm flex items-center gap-2">Tax ID {vatStatus && <VatBadge status={vatStatus} />}</Label>
            <div className="mt-1.5 flex gap-2">
              <Input
                data-testid="client-nip-input"
                value={form.nip}
                maxLength={15}
                onChange={(e) => { set("nip", e.target.value); setVatStatus(null); }}
                placeholder={isPoland ? "NIP — wpisz, aby pobrać dane firmy" : "VAT / Tax ID (do 15 znaków)"}
                className={nipInvalid ? "border-red-400 focus-visible:ring-red-400" : ""}
                autoFocus
              />
              {isPoland && (
                <Button type="button" variant="outline" onClick={lookupNip} disabled={nipChecking} data-testid="client-nip-lookup-btn" className="shrink-0 whitespace-nowrap">
                  {nipChecking ? "…" : "Pobierz dane"}
                </Button>
              )}
            </div>
            {nipInvalid ? (
              <p className="text-xs text-red-500 font-[Manrope] mt-1" data-testid="client-nip-error">Nieprawidłowa suma kontrolna NIP</p>
            ) : (
              isPoland && <p className="text-xs text-muted-foreground font-[Manrope] mt-1">Wpisz NIP i kliknij „Pobierz dane", aby automatycznie uzupełnić nazwę, adres, REGON, KRS i status VAT z publicznych rejestrów (MF / Biała Lista). Wszystko można później edytować.</p>
            )}
          </div>
          <div className="sm:col-span-2">
            <Label className="font-[Manrope] text-sm">Company Name *</Label>
            <Input
              data-testid="client-company-input"
              value={form.company_name}
              onChange={(e) => set("company_name", e.target.value)}
              placeholder="Acme Sp. z o.o."
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="font-[Manrope] text-sm">Contact Person</Label>
            <Input
              data-testid="client-contact-input"
              value={form.contact_person}
              onChange={(e) => set("contact_person", e.target.value)}
              placeholder="Jane Doe"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="font-[Manrope] text-sm">Phone</Label>
            <Input
              data-testid="client-phone-input"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              placeholder="+48 22 000 0000"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="font-[Manrope] text-sm">Email</Label>
            <Input
              data-testid="client-email-input"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="contact@company.com"
              className="mt-1.5"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-[Manrope] text-sm">Region</Label>
              <Select value={form.region} onValueChange={(v) => set("region", v)}>
                <SelectTrigger data-testid="client-region-select" className="mt-1.5 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="Poland">Poland</SelectItem>
                  <SelectItem value="Kenya">Kenya</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-[Manrope] text-sm">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="client-status-select" className="mt-1.5 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="Lead">Lead</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="font-[Manrope] text-sm">Kraj</Label>
            {countryCustom ? (
              <div className="mt-1.5 flex gap-2">
                <Input
                  data-testid="client-country-input"
                  value={form.country}
                  onChange={(e) => set("country", e.target.value)}
                  placeholder="Wpisz kraj…"
                  autoFocus
                />
                <Button type="button" variant="outline" className="shrink-0" onClick={() => { setCountryCustom(false); set("country", ""); }}>
                  Lista
                </Button>
              </div>
            ) : (
              <Select
                value={form.country || undefined}
                onValueChange={(v) => { if (v === "__custom__") { setCountryCustom(true); set("country", ""); } else set("country", v); }}
              >
                <SelectTrigger data-testid="client-country-select" className="mt-1.5 bg-white">
                  <SelectValue placeholder="Wybierz kraj…" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {COUNTRY_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                  <SelectItem value="__custom__">➕ Inny (wpisz…)</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-[Manrope] text-sm">Abonament</Label>
              <Select value={form.subscription} onValueChange={(v) => set("subscription", v)}>
                <SelectTrigger data-testid="client-subscription-select" className="mt-1.5 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="None">Brak</SelectItem>
                  <SelectItem value="Standard">Standard (★★)</SelectItem>
                  <SelectItem value="Premium">Premium (★★★)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-[Manrope] text-sm">Wartość roczna (PLN)</Label>
              <Input
                type="number"
                data-testid="client-annual-value-input"
                value={form.annual_value}
                onChange={(e) => set("annual_value", parseFloat(e.target.value) || 0)}
                placeholder="0"
                className="mt-1.5"
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className="font-[Manrope] text-sm">Adres</Label>
            <Input
              data-testid="client-address-input"
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              placeholder="ul. Przykładowa 1, 00-001 Warszawa"
              className="mt-1.5"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="font-[Manrope] text-sm">Źródło kontaktu</Label>
              <Select value={form.source || undefined} onValueChange={(v) => set("source", v)}>
                <SelectTrigger data-testid="client-source-select" className="mt-1.5 bg-white">
                  <SelectValue placeholder="Wybierz…" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {SOURCE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {isPoland && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-[Manrope] text-sm">REGON</Label>
              <Input
                data-testid="client-regon-input"
                value={form.regon}
                onChange={(e) => set("regon", e.target.value)}
                placeholder="000000000"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="font-[Manrope] text-sm">KRS</Label>
              <Input
                data-testid="client-krs-input"
                value={form.krs}
                onChange={(e) => set("krs", e.target.value)}
                placeholder="0000000000"
                className="mt-1.5"
              />
            </div>
          </div>
          )}
          {isEU ? (
          <div className="sm:col-span-2">
            <Label className="font-[Manrope] text-sm">Numer konta bankowego (IBAN)</Label>
            <Input
              data-testid="client-bank-input"
              value={form.bank_account}
              maxLength={42}
              onChange={(e) => set("bank_account", formatIban(e.target.value))}
              placeholder="PL00 0000 0000 0000 0000 0000 0000"
              className={`mt-1.5 tabular-nums ${bankInvalid ? "border-red-400 focus-visible:ring-red-400" : ""}`}
            />
            {bankInvalid ? (
              <p className="text-xs text-red-500 font-[Manrope] mt-1" data-testid="client-bank-error">Nieprawidłowy numer konta (błędna suma kontrolna IBAN)</p>
            ) : (
              ibanValid(form.bank_account) && <p className="text-xs text-emerald-600 font-[Manrope] mt-1" data-testid="client-bank-ok">Numer konta poprawny ✓</p>
            )}
          </div>
          ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-[Manrope] text-sm">SWIFT / BIC</Label>
              <Input
                data-testid="client-swift-input"
                value={form.swift_bic}
                maxLength={11}
                onChange={(e) => set("swift_bic", e.target.value.toUpperCase())}
                placeholder="np. BARCKENX"
                className="mt-1.5 uppercase"
              />
            </div>
            <div>
              <Label className="font-[Manrope] text-sm">Numer konta</Label>
              <Input
                data-testid="client-account-input"
                value={form.bank_account}
                maxLength={40}
                onChange={(e) => set("bank_account", e.target.value)}
                placeholder="Numer konta / IBAN"
                className="mt-1.5"
              />
            </div>
          </div>
          )}
          <div className="sm:col-span-2">
            <Label className="font-[Manrope] text-sm">Notes</Label>
            <div className="mt-1.5">
              <RichTextEditor value={form.notes} onChange={(v) => set("notes", v)} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="client-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="client-save-btn"
            className="bg-[#193962] hover:bg-[#12294A] text-white transition-all duration-200 hover:-translate-y-[1px]"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
